import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useAuth } from '../../app/auth-context';
import { ApiError, api } from '../../lib/api';

/**
 * Membros e convites.
 *
 * O token do convite aparece uma unica vez, no momento da criacao. Enquanto
 * o envio por e-mail nao existe (Fase 5), quem convida copia o link.
 */
export function HouseholdPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canInvite = user?.role === 'OWNER' || user?.role === 'ADMIN';

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'ADMIN' | 'MEMBER' | 'VIEWER'>('MEMBER');
  const [issuedLink, setIssuedLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const members = useQuery({ queryKey: ['members'], queryFn: api.members });
  const invites = useQuery({
    queryKey: ['invites'],
    queryFn: api.invites,
    enabled: canInvite,
  });

  const createInvite = useMutation({
    mutationFn: () => api.createInvite({ email, role }),
    onSuccess: (invite) => {
      setIssuedLink(`${window.location.origin}/convite/${invite.token}`);
      setEmail('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['invites'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiError ? caught.message : 'Nao foi possivel convidar.'),
  });

  const revokeInvite = useMutation({
    mutationFn: api.revokeInvite,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['invites'] }),
  });

  return (
    <div className="main">
      <header className="page-header">
        <h1>Casa</h1>
        <p>
          Quem participa das financas. Cada pessoa entra com login proprio, e os dados bancarios de
          cada uma dependem do consentimento dela.
        </p>
      </header>

      <section className="card">
        <h2 className="card-title">Membros</h2>
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th>Perfil</th>
                <th>2FA</th>
              </tr>
            </thead>
            <tbody>
              {members.data?.members.map((member) => (
                <tr key={member.userId}>
                  <td>
                    {member.name}
                    {member.isYou && (
                      <>
                        {' '}
                        <span className="badge accent">voce</span>
                      </>
                    )}
                  </td>
                  <td className="muted">{member.email}</td>
                  <td>
                    <span className="badge">{traduzirPerfil(member.role)}</span>
                  </td>
                  <td>{member.totpEnabled ? 'Ativa' : <span className="muted">Inativa</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {canInvite && (
        <section className="card">
          <h2 className="card-title">Convidar</h2>
          <p className="card-hint">
            O link aparece uma unica vez e vale por 7 dias. Entregue por um canal que voce confie.
          </p>

          {error && (
            <div className="alert error" role="alert">
              {error}
            </div>
          )}

          {issuedLink && (
            <div className="alert success">
              <p style={{ marginBottom: 'var(--space-2)' }}>Convite criado. Copie agora:</p>
              <p className="mono" style={{ wordBreak: 'break-all' }}>
                {issuedLink}
              </p>
              <button
                className="btn secondary"
                style={{ marginTop: 'var(--space-3)' }}
                onClick={() => navigator.clipboard.writeText(issuedLink)}
              >
                Copiar link
              </button>
            </div>
          )}

          <div className="field">
            <label htmlFor="inviteEmail">E-mail</label>
            <input
              id="inviteEmail"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <div className="field">
            <label htmlFor="inviteRole">Perfil</label>
            <select
              id="inviteRole"
              value={role}
              onChange={(event) => setRole(event.target.value as typeof role)}
            >
              <option value="MEMBER">Membro — lanca e edita</option>
              <option value="ADMIN">Administrador — tambem convida</option>
              <option value="VIEWER">Leitura — apenas consulta</option>
            </select>
          </div>

          <button
            className="btn"
            onClick={() => createInvite.mutate()}
            disabled={!email.includes('@') || createInvite.isPending}
          >
            {createInvite.isPending ? 'Criando...' : 'Criar convite'}
          </button>

          {(invites.data?.invites.length ?? 0) > 0 && (
            <div className="table-scroll" style={{ marginTop: 'var(--space-6)' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>E-mail</th>
                    <th>Perfil</th>
                    <th>Situacao</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {invites.data?.invites.map((invite) => (
                    <tr key={invite.id}>
                      <td>{invite.email}</td>
                      <td>{traduzirPerfil(invite.role)}</td>
                      <td>
                        <span className="badge">{invite.status}</span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {invite.status === 'pendente' && (
                          <button
                            className="btn danger"
                            onClick={() => revokeInvite.mutate(invite.id)}
                          >
                            Revogar
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function traduzirPerfil(role: string): string {
  const mapa: Record<string, string> = {
    OWNER: 'Dono',
    ADMIN: 'Administrador',
    MEMBER: 'Membro',
    VIEWER: 'Leitura',
  };
  return mapa[role] ?? role;
}
